import * as Dialog from '@radix-ui/react-dialog';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from './cn';

const MODAL_MARGIN = 32; // окно не доходит до краёв экрана на 16px сверху и снизу

// Radix даёт Escape, клик вне окна, ловушку фокуса и ARIA; здесь только вид.
// Три зоны: заголовок с head (вкладки, баннеры) и footer (кнопки) закреплены, прокручивается только тело.
export function Modal({
  open, onClose, title, head, footer, children, width = 'max-w-lg',
}: { open: boolean; onClose: () => void; title: string; head?: ReactNode; footer?: ReactNode; children: ReactNode; width?: string }) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          className={cn(
            'fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] focus:outline-none',
            'bg-paper rounded-xl shadow-xl border border-line flex flex-col', width,
          )}
          style={{ maxHeight: `calc(100vh - ${MODAL_MARGIN}px)` }}
          // Escape внутри открытого выпадашки (выбор проекта, подсказки исполнителей) закрывает её, а не окно
          onEscapeKeyDown={(e) => { if ((e.target as HTMLElement | null)?.closest?.('[data-keeps-escape]') || document.querySelector('[data-keeps-escape]')) e.preventDefault(); }}
        >
          <div className="px-6 pt-5 shrink-0">
            <Dialog.Title className="text-xl font-bold mb-3">{title}</Dialog.Title>
            <Dialog.Description className="sr-only">{title}</Dialog.Description>
            {head}
          </div>
          <div data-modal-body className="px-6 pb-5 flex-1 min-h-0 overflow-y-auto scroller">{children}</div>
          {footer && <div className="px-6 py-3 border-t border-line shrink-0">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Вкладки тела: все панели смонтированы и лежат в одной клетке, видна одна. Высота клетки — самая
 * высокая панель (окно не прыгает при переключении), но не больше, чем влезает в окно; каждая панель
 * прокручивает только своё содержимое, короткая ничего не прокручивает.
 */
export function TabPanels({ active, panels }: { active: string; panels: [string, ReactNode][] }) {
  const root = useRef<HTMLDivElement>(null);
  const inner = useRef(new Map<string, HTMLDivElement>());
  const [height, setHeight] = useState<number | undefined>();
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const measure = () => {
      const natural = Math.max(0, ...[...inner.current.values()].map((d) => d.offsetHeight)) + 8; // + поле панели
      const dialog = el.closest<HTMLElement>('[role="dialog"]');
      const body = el.parentElement;
      if (!dialog || !body) { setHeight(natural); return; }
      // всё в окне кроме тела (заголовок, вкладки, кнопки) плюс отступы тела: не зависит от высоты клетки,
      // и считается верно и когда тело уже обрезано окном, и когда нет
      const cs = getComputedStyle(body);
      const chrome = dialog.getBoundingClientRect().height - body.clientHeight + parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      setHeight(Math.min(natural, Math.floor(window.innerHeight - MODAL_MARGIN - chrome)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    for (const d of inner.current.values()) ro.observe(d);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [panels.length]);
  return (
    <div ref={root} className="grid grid-rows-[minmax(0,1fr)]" style={{ height }}>
      {panels.map(([key, node]) => (
        <div key={key} className={cn('col-start-1 row-start-1 min-h-0 overflow-y-auto scroller -m-1 p-1', key !== active && 'invisible')} aria-hidden={key !== active}>
          <div ref={(d) => { if (d) inner.current.set(key, d); else inner.current.delete(key); }}>{node}</div>
        </div>
      ))}
    </div>
  );
}
